import React, { useState, useEffect } from 'react';
import { productService } from '@/application/services';
import { Product, ProductSize } from '@/domain/entities';

interface ProductSelectorProps {
  onSelectProduct: (product: Product, size: ProductSize) => void;
}

export const ProductSelector: React.FC<ProductSelectorProps> = ({ onSelectProduct }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [productSizes, setProductSizes] = useState<ProductSize[]>([]);
  const [loadingSizes, setLoadingSizes] = useState(false);

  useEffect(() => {
    loadProducts();
  }, [searchQuery]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await productService.getProducts({
        search: searchQuery || undefined,
        limit: 20,
        offset: 0,
      });
      setProducts(response.products);
    } catch (err) {
      setError('Не удалось загрузить товары');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleProductClick = async (product: Product) => {
    setSelectedProduct(product);
    setLoadingSizes(true);
    try {
      const response = await productService.getProduct(product.sku);
      setProductSizes(response.sizes);
    } catch (err) {
      setError('Не удалось загрузить размеры товара');
      console.error(err);
    } finally {
      setLoadingSizes(false);
    }
  };

  const handleSizeSelect = (size: ProductSize) => {
    if (selectedProduct) {
      onSelectProduct(selectedProduct, size);
      // Сбрасываем выбор
      setSelectedProduct(null);
      setProductSizes([]);
    }
  };

  const handleBack = () => {
    setSelectedProduct(null);
    setProductSizes([]);
  };

  if (selectedProduct) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBack}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
          >
            <svg className="w-5 h-5 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {selectedProduct.name}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {selectedProduct.brand} • SKU: {selectedProduct.sku}
            </p>
          </div>
        </div>

        {loadingSizes ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          </div>
        ) : (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Выберите размер:
            </h4>
            <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
              {productSizes.map((size) => (
                <button
                  key={size.id}
                  onClick={() => handleSizeSelect(size)}
                  disabled={!size.inStock && !size.isPreOrder}
                  className={`p-3 border rounded-md text-left transition-colors ${
                    size.inStock || size.isPreOrder
                      ? 'border-gray-300 dark:border-gray-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:border-indigo-500'
                      : 'border-gray-200 dark:border-gray-700 opacity-50 cursor-not-allowed'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {size.us}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {size.price.toLocaleString('ru-RU')} ₽
                      </div>
                      {size.isPreOrder && (
                        <div className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                          Под заказ {size.deliveryDays ? `(${size.deliveryDays} дн.)` : ''}
                        </div>
                      )}
                      {!size.inStock && !size.isPreOrder && (
                        <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                          Нет в наличии
                        </div>
                      )}
                    </div>
                    {size.quantity > 0 && (
                      <div className="text-xs text-green-600 dark:text-green-400">
                        ×{size.quantity}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Поиск */}
      <div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Поиск по названию, бренду или SKU..."
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* Список товаров */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
        </div>
      ) : error ? (
        <div className="text-center py-8 text-red-600 dark:text-red-400">
          {error}
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          Товары не найдены
        </div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {products.map((product) => (
            <button
              key={product.sku}
              onClick={() => handleProductClick(product)}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left flex items-center gap-3"
            >
              <img
                src={product.imageUrl}
                alt={product.name}
                className="w-16 h-16 object-cover rounded"
                onError={(e) => {
                  e.currentTarget.src = '/placeholder.png';
                }}
              />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 dark:text-white truncate">
                  {product.name}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {product.brand}
                </div>
                <div className="text-xs text-gray-400 dark:text-gray-500">
                  SKU: {product.sku}
                </div>
              </div>
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

